import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "../../lib/api";
import {
  Dumbbell, Trophy, Wallet, ShieldCheck, Plus, Trash2, CheckCircle2,
  XCircle, Clock, ChevronDown, ChevronUp, AlertTriangle, HeartPulse,
} from "lucide-react";

const inputCls = "w-full bg-black/40 border border-zinc-700 rounded-lg px-3 py-2 text-sm focus:border-amber-500 focus:outline-none";
const fmt = (n) => (n || 0).toLocaleString("fr-CA", { style: "currency", currency: "CAD" });

const STATUT_BADGE = {
  approuve: { label: "Approuvé", cls: "bg-emerald-500/20 text-emerald-300", Icon: CheckCircle2 },
  refuse: { label: "Refusé", cls: "bg-red-500/20 text-red-300", Icon: XCircle },
  en_attente: { label: "En attente", cls: "bg-amber-500/20 text-amber-300", Icon: Clock },
};

function AthleteCard({ a, competitions, reload }) {
  const [open, setOpen] = useState(false);
  const [bl, setBl] = useState({ label: "", type: "cout", montant: "", competition: "" });
  const [val, setVal] = useState({ competition: competitions[0]?.nom || "", statut: "approuve", couverture_ok: false, note: "" });

  const addBudget = async () => {
    if (!bl.label || !bl.montant) return toast.error("Libellé et montant requis.");
    try {
      await api.post(`/athletes-center/${a.id}/budget`, { ...bl, montant: parseFloat(bl.montant) });
      setBl({ label: "", type: "cout", montant: "", competition: "" });
      toast.success("Ligne ajoutée"); reload();
    } catch { toast.error("Échec de l'ajout de la ligne."); }
  };
  const delBudget = async (id) => { try { await api.delete(`/athletes-center/budget/${id}`); reload(); } catch { toast.error("Suppression impossible."); } };
  const saveVal = async () => {
    if (!val.competition) return toast.error("Choisis une compétition.");
    try {
      await api.post(`/athletes-center/${a.id}/validation`, val);
      toast.success("Validation enregistrée"); reload();
    } catch { toast.error("Échec de la validation."); }
  };
  const delVal = async (id) => { try { await api.delete(`/athletes-center/validation/${id}`); reload(); } catch { toast.error("Suppression impossible."); } };

  const reste = a.budget.reste_a_charge;
  return (
    <div className="rounded-xl border border-zinc-800 bg-zinc-950/60 overflow-hidden" data-testid={`athlete-card-${a.id}`}>
      <div className="p-4 flex items-start justify-between gap-3 cursor-pointer" onClick={() => setOpen(!open)}>
        <div className="flex items-center gap-3 min-w-0">
          {a.photo_url ? <img src={a.photo_url} alt="" className="w-11 h-11 rounded-full object-cover" /> :
            <div className="w-11 h-11 rounded-full bg-zinc-800 flex items-center justify-center text-amber-400 font-bold">{(a.full_name || "?")[0]}</div>}
          <div className="min-w-0">
            <div className="font-semibold truncate">{a.full_name}</div>
            <div className="text-xs text-zinc-500">{a.sport || "—"} · {a.level || "—"} · <span className="text-zinc-300">{a.record}</span></div>
            <div className="flex flex-wrap gap-1.5 mt-1.5">
              {a.suspension_active && <span className="text-[10px] bg-red-500/20 text-red-300 px-1.5 py-0.5 rounded inline-flex items-center gap-1"><AlertTriangle size={10} /> Suspension</span>}
              {a.validations?.slice(0, 2).map((v) => {
                const b = STATUT_BADGE[v.statut] || STATUT_BADGE.en_attente;
                return <span key={v.id} className={`text-[10px] px-1.5 py-0.5 rounded inline-flex items-center gap-1 ${b.cls}`}><b.Icon size={10} /> {v.competition}</span>;
              })}
            </div>
          </div>
        </div>
        <div className="text-right shrink-0">
          <div className={`text-sm font-bold ${reste > 0 ? "text-red-300" : "text-emerald-300"}`}>{fmt(reste)}</div>
          <div className="text-[10px] text-zinc-500">reste à charge</div>
          {open ? <ChevronUp size={16} className="text-zinc-500 ml-auto mt-1" /> : <ChevronDown size={16} className="text-zinc-500 ml-auto mt-1" />}
        </div>
      </div>

      {open && (
        <div className="border-t border-zinc-800 p-4 space-y-5 bg-black/30">
          {/* BUDGET */}
          <div>
            <div className="flex items-center gap-2 text-sm font-semibold text-zinc-300 mb-2"><Wallet size={15} className="text-amber-400" /> Budget · coûts {fmt(a.budget.total_couts)} · financement {fmt(a.budget.total_financement)}</div>
            <div className="space-y-1.5 mb-3">
              {(a.budget.lines || []).map((l) => (
                <div key={l.id} className="flex items-center justify-between text-xs bg-zinc-900/60 rounded px-3 py-2" data-testid={`budget-line-${l.id}`}>
                  <div><span className={`uppercase text-[10px] mr-2 ${l.type === "cout" ? "text-red-300" : "text-emerald-300"}`}>{l.type === "cout" ? "coût" : "financ."}</span>{l.label}{l.competition && <span className="text-zinc-500"> · {l.competition}</span>}</div>
                  <div className="flex items-center gap-2"><span className="font-mono">{fmt(l.montant)}</span><button onClick={() => delBudget(l.id)} className="text-zinc-500 hover:text-red-400"><Trash2 size={12} /></button></div>
                </div>
              ))}
              {!(a.budget.lines || []).length && <div className="text-xs text-zinc-600 italic">Aucune ligne de budget.</div>}
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-5 gap-2">
              <input className={`${inputCls} sm:col-span-2`} placeholder="Libellé (ex : voyage)" value={bl.label} onChange={(e) => setBl({ ...bl, label: e.target.value })} data-testid={`budget-label-${a.id}`} />
              <select className={inputCls} value={bl.type} onChange={(e) => setBl({ ...bl, type: e.target.value })} data-testid={`budget-type-${a.id}`}><option value="cout">Coût</option><option value="financement">Financement</option></select>
              <input type="number" className={inputCls} placeholder="$" value={bl.montant} onChange={(e) => setBl({ ...bl, montant: e.target.value })} data-testid={`budget-montant-${a.id}`} />
              <button onClick={addBudget} className="inline-flex items-center justify-center gap-1 bg-amber-500 hover:bg-amber-400 text-black text-xs font-bold rounded-lg px-2" data-testid={`budget-add-${a.id}`}><Plus size={13} /> Ajouter</button>
            </div>
          </div>

          {/* VALIDATION */}
          <div>
            <div className="flex items-center gap-2 text-sm font-semibold text-zinc-300 mb-2"><ShieldCheck size={15} className="text-amber-400" /> Validation de participation / déplacement</div>
            <div className="space-y-1.5 mb-3">
              {(a.validations || []).map((v) => {
                const b = STATUT_BADGE[v.statut] || STATUT_BADGE.en_attente;
                return (
                  <div key={v.id} className="flex items-center justify-between text-xs bg-zinc-900/60 rounded px-3 py-2" data-testid={`validation-${v.id}`}>
                    <div className="flex items-center gap-2"><span className={`px-1.5 py-0.5 rounded inline-flex items-center gap-1 ${b.cls}`}><b.Icon size={11} /> {b.label}</span><span>{v.competition}</span>{v.couverture_ok && <span className="text-emerald-400">· couverture ✓</span>}{v.note && <span className="text-zinc-500 italic">· {v.note}</span>}</div>
                    <button onClick={() => delVal(v.id)} className="text-zinc-500 hover:text-red-400"><Trash2 size={12} /></button>
                  </div>
                );
              })}
              {!(a.validations || []).length && <div className="text-xs text-zinc-600 italic">Aucune validation.</div>}
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 items-center">
              <select className={inputCls} value={val.competition} onChange={(e) => setVal({ ...val, competition: e.target.value })} data-testid={`val-comp-${a.id}`}>
                {competitions.map((c) => <option key={c.id} value={c.nom}>{c.nom}</option>)}
              </select>
              <select className={inputCls} value={val.statut} onChange={(e) => setVal({ ...val, statut: e.target.value })} data-testid={`val-statut-${a.id}`}><option value="approuve">Approuver</option><option value="en_attente">En attente</option><option value="refuse">Refuser</option></select>
              <label className="flex items-center gap-1.5 text-xs text-zinc-300"><input type="checkbox" checked={val.couverture_ok} onChange={(e) => setVal({ ...val, couverture_ok: e.target.checked })} data-testid={`val-couverture-${a.id}`} /> Couverture instructeur</label>
              <button onClick={saveVal} className="inline-flex items-center justify-center gap-1 bg-zinc-800 hover:bg-zinc-700 text-white text-xs font-bold rounded-lg px-2 py-2" data-testid={`val-save-${a.id}`}><CheckCircle2 size={13} /> Valider</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default function AthletesCenterPanel() {
  const [data, setData] = useState(null);
  const [q, setQ] = useState("");
  const load = () => api.get("/athletes-center/overview").then((r) => setData(r.data)).catch(() => toast.error("Chargement impossible"));
  useEffect(() => { load(); }, []);

  if (!data) return <div className="text-zinc-500 p-8">Chargement…</div>;
  const { athletes, competitions, totals } = data;
  const filtered = athletes.filter((a) => (a.full_name || "").toLowerCase().includes(q.toLowerCase()));

  return (
    <div className="space-y-6" data-testid="athletes-center-module">
      <div className="border border-zinc-800 rounded-2xl p-6 bg-gradient-to-br from-zinc-950 to-black">
        <div className="text-amber-400 text-[10px] tracking-[0.4em] mb-2 flex items-center gap-2"><Dumbbell size={13} /> CENTRE ATHLÈTES &amp; COMPÉTITIONS</div>
        <h1 className="text-3xl font-bold tracking-tight">Athlètes, compétitions &amp; budgets</h1>
        <p className="text-zinc-500 text-sm mt-2 max-w-2xl">Valide les participations/déplacements et suis le budget par athlète (coûts vs financement/commandites).</p>
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 mt-5">
          {[
            ["Athlètes", totals.nb_athletes],
            ["Compétitions", totals.nb_competitions],
            ["Coûts totaux", fmt(totals.total_couts)],
            ["Financement", fmt(totals.total_financement)],
            ["Validations en attente", totals.validations_en_attente],
          ].map(([l, v]) => (
            <div key={l} className="rounded-xl border border-zinc-800 bg-black/40 p-3 text-center"><div className="text-xl font-bold text-amber-400">{v}</div><div className="text-[10px] uppercase text-zinc-500 tracking-wider">{l}</div></div>
          ))}
        </div>
        <div className={`mt-3 rounded-xl border p-3 text-sm ${totals.reste_a_charge_global > 0 ? "border-red-500/30 bg-red-500/[0.06] text-red-200" : "border-emerald-500/30 bg-emerald-500/[0.06] text-emerald-200"}`} data-testid="reste-global">
          Reste à charge global (dojo) : <b>{fmt(totals.reste_a_charge_global)}</b>
        </div>
      </div>

      {/* Compétitions */}
      <div className="rounded-2xl border border-zinc-800 bg-zinc-950/40 p-5" data-testid="ac-competitions">
        <div className="flex items-center gap-2 text-sm font-semibold mb-4"><Trophy size={16} className="text-amber-400" /> Compétitions ({competitions.length})</div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {competitions.map((c) => (
            <div key={c.id} className="rounded-xl border border-zinc-800 bg-black/40 p-3" data-testid={`ac-comp-${c.id}`}>
              <div className="text-sm font-semibold">{c.nom}</div>
              <div className="text-xs text-zinc-500 mt-1">{c.date || "date à confirmer"}{c.lieu ? ` · ${c.lieu}` : ""}</div>
              <span className="text-[10px] uppercase tracking-wider text-amber-400/70">{c.source === "jeux_martiaux" ? "Jeux Martiaux" : "Compétition"}</span>
            </div>
          ))}
          {!competitions.length && <div className="text-xs text-zinc-600 italic">Aucune compétition. Crée-en dans « Jeux Martiaux Inter-Écoles ».</div>}
        </div>
      </div>

      {/* Athlètes */}
      <div className="rounded-2xl border border-zinc-800 bg-zinc-950/40 p-5" data-testid="ac-athletes">
        <div className="flex items-center justify-between gap-3 mb-4 flex-wrap">
          <div className="flex items-center gap-2 text-sm font-semibold"><Dumbbell size={16} className="text-amber-400" /> Athlètes ({filtered.length})</div>
          <input className={`${inputCls} max-w-xs`} placeholder="Rechercher un athlète…" value={q} onChange={(e) => setQ(e.target.value)} data-testid="ac-search" />
        </div>
        <div className="grid lg:grid-cols-2 gap-3">
          {filtered.map((a) => <AthleteCard key={a.id} a={a} competitions={competitions} reload={load} />)}
        </div>
        {!filtered.length && <div className="text-sm text-zinc-600 italic">Aucun athlète. Ajoute-les dans le module « Athlètes ».</div>}
      </div>
    </div>
  );
}
